import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import InfoIcon from "../../../assets/icons/InfoIcon";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UnlockedIcon from "../../../assets/icons/UnlockedIcon";
import LockedPlusIcon from "../../../assets/icons/LockPlusIcon";
import TertiaryButton from "../../Buttons/TertiaryButton/TertiaryButton";
import useShotClockTimer from "../../../hooks/useShotClockTimer/useShotClockTimer";
import { parseMcleodDateWithOffset } from "../../../utils/dateHelpers";
import { LockedLoadsContext } from "../../Dashboard/Dashboard";

export default function BarShotClock({
  activeUser,
  isLoading,
  load,
  handleLockLoad,
  setAvailableToBook,
  actionCallback
}) {
  const [infoText, setInfoText] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  const [loadLocked, setLoadLocked] = useState({});
  const [userLocked, setUserLocked] = useState(false);
  const lockedLoads = useContext(LockedLoadsContext);
  const { timeRemaining } = useShotClockTimer(loadLocked, expirationTime);

  useEffect(() => {
    const userLock = loadLocked?.lockedUserId === activeUser?.userIdMcld;
    setUserLocked(userLock);

    if (loadLocked) {
      setInfoText(userLock ?
        "You are locked into this load" :
        `This load is currently locked by ${loadLocked.lockedUserId}`
      );
      setAvailableToBook(userLock);
    } else {
      setInfoText("This load is currently unlocked");
      setAvailableToBook(true);
    }

    if (loadLocked && loadLocked.unlockTime) {
      setExpirationTime(parseMcleodDateWithOffset(loadLocked.unlockTime));
    } else {
      setExpirationTime("");
    }
  }, [loadLocked, activeUser]);

  useEffect(() => {
    setLoadLocked(lockedLoads?.find(ll => ll.id == load.movementId));
  }, [lockedLoads, load]);

  return (
    <>
      {isLoading &&
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          padding: "10px",
          backgroundColor: "black_opacity.05",
          color: "black_opacity.100",
        }}
        >
          <CircularProgress sx={{ color: "primary.60" }} size={20} />
        </Box>
      }
      {!isLoading && !!infoText &&
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px",
            color: "black_opacity.100",
            ...(userLocked ? { backgroundColor: "primary.10" } : loadLocked ? { backgroundColor: "warning.10" } : { backgroundColor: "black_opacity.10" })
          }}
        >
          <Box sx={{ display: "flex" }}>
            {loadLocked && !userLocked ?
              <WarningAmberIcon sx={{ fontSize: "20px", color: "warning.80", marginRight: "5px" }} />
              :
              <InfoIcon sx={{ fontSize: "20px", color: "black_opacity.80", marginRight: "5px" }} />
            }
            <Typography variant="body1">{infoText}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {loadLocked ?
              <>
                <Typography variant="body1" color="black_opacity.100">
                  {timeRemaining}
                </Typography>
                <TertiaryButton
                  label="View Queue"
                  startIcon={LockedPlusIcon}
                  onClick={() => actionCallback({ action: "shot-clock-locked", load: load, expirationTime: expirationTime, loadLocked: loadLocked })}
                />
              </>
              :
              <TertiaryButton
                label="Lock Load"
                startIcon={UnlockedIcon}
                onClick={handleLockLoad}
              />
            }
          </Box>
        </Box>
      }
    </>
  );
}

BarShotClock.propTypes = {
  activeUser: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  load: PropTypes.object,
  handleLockLoad: PropTypes.func.isRequired,
  setAvailableToBook: PropTypes.func.isRequired,
  actionCallback: PropTypes.func.isRequired
};